import React, { useCallback, useState, useEffect, useMemo } from "react";
import { Checkbox, IconButton, Stack, Text } from "@fluentui/react";
import ChevronRight from "@mui/icons-material/ChevronRight";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useMetadataContext } from "../../providers/MetadataProvider";
import { useUseCaseContext } from "../../hooks/UseCaseContext";
import { getUseCases, MetadataFolder, UseCase } from "../../api";
import { DEFAULT_FOLDER_LABEL } from "../../utils/constants";
import { getRestrictedFolderView } from "../../utils/folderRestrictions";
import { CLOSE_ICONS, IconButton as IconButtonCustom } from "../IconButton";
import styles from "./LibraryPanel.module.css";

interface LibraryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentDg: string;
  onFolderStructureChanged: (folderNumber: number) => void;
}

const LibraryPanel: React.FC<LibraryPanelProps> = ({
  isOpen,
  onClose,
  currentDg,
  onFolderStructureChanged,
}) => {
  const { useCase } = useUseCaseContext();
  const {
    metadataFolders,
    selectedMetadata,
    excludedMetadata,
    fetchingMetadata,
    metadataError,
    setExcludedMetadata,
    toggleFolderSelection,
    isMetadataSelected,
  } = useMetadataContext();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );
  const [useCaseConfig, setUseCaseConfig] = useState<UseCase | null>(null);

  // Re-read on DG switch: the use case cache is invalidated at the same time.
  useEffect(() => {
    let cancelled = false;
    getUseCases().then((useCases) => {
      if (!cancelled) {
        setUseCaseConfig(useCases.find((uc) => uc.id === useCase) ?? null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [useCase, currentDg]);

  const restrictions = useMemo(() => {
    const configured = useCaseConfig?.virtualLibraryAvailableFolders ?? [];
    if (configured.length > 0) return configured;
    return [];
  }, [useCaseConfig]);

  const { folders: filteredFolders, parentPath: restrictionParentPath } =
    useMemo(
      () => getRestrictedFolderView(metadataFolders, restrictions),
      [metadataFolders, restrictions],
    );

  // Reset metadata selection when use case changes to ensure clean state
  useEffect(() => {
    if (metadataFolders.length > 0) {
      // Get all possible paths from the current filtered view
      const getFilteredPaths = (
        folders: MetadataFolder[],
        parentPath = "",
      ): string[] => {
        const paths: string[] = [];
        folders.forEach((folder) => {
          const currentPath = parentPath
            ? `${parentPath}/${folder.folderName}`
            : folder.folderName;
          paths.push(currentPath);
          if (folder.subfolders && folder.subfolders.length > 0) {
            paths.push(...getFilteredPaths(folder.subfolders, currentPath));
          }
        });
        return paths;
      };

      const allAvailablePaths = getFilteredPaths(metadataFolders);

      const currentFilteredPaths = getFilteredPaths(
        filteredFolders,
        restrictionParentPath,
      );

      // Reset to select all folders in the current filtered view
      // by excluding all paths that are NOT in the current filtered view
      const newExcluded = allAvailablePaths.filter(
        (path) => !currentFilteredPaths.includes(path),
      );
      setExcludedMetadata(newExcluded);
    }
    // filteredFolders is memoised, so depending on it re-applies the restrictions
    // once the use case config resolves without re-running every render.
  }, [useCase, metadataFolders, filteredFolders, restrictionParentPath]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Get all child paths for a folder
  const getAllChildPaths = useCallback(
    (folder: MetadataFolder, parentPath = ""): string[] => {
      const currentPath = parentPath
        ? `${parentPath}/${folder.folderName}`
        : folder.folderName;
      const paths = [currentPath];

      if (folder.subfolders) {
        folder.subfolders.forEach((subfolder) => {
          paths.push(...getAllChildPaths(subfolder, currentPath));
        });
      }

      return paths;
    },
    [],
  );

  // Get checkbox state for a folder (checked, unchecked, or indeterminate)
  const getCheckboxState = useCallback(
    (folder: MetadataFolder, folderPath: string, parentPath = "") => {
      const allChildPaths = getAllChildPaths(folder, parentPath);
      const selectedChildren = allChildPaths.filter((path) =>
        isMetadataSelected(path),
      );

      if (selectedChildren.length === 0) {
        return { checked: false, indeterminate: false };
      } else if (selectedChildren.length === allChildPaths.length) {
        return { checked: true, indeterminate: false };
      } else {
        return { checked: false, indeterminate: true };
      }
    },
    [isMetadataSelected, getAllChildPaths],
  );

  // Get all paths from the filtered folders
  const getFilteredFolderPaths = useCallback(
    (folders: MetadataFolder[], parentPath = ""): string[] => {
      const paths: string[] = [];
      folders.forEach((folder) => {
        const currentPath = parentPath
          ? `${parentPath}/${folder.folderName}`
          : folder.folderName;
        paths.push(currentPath);
        if (folder.subfolders && folder.subfolders.length > 0) {
          paths.push(...getFilteredFolderPaths(folder.subfolders, currentPath));
        }
      });
      return paths;
    },
    [],
  );

  // Get only the deepest folders from selection that are visible in current filter
  const getDeepestFolders = useCallback(() => {
    const visiblePaths = getFilteredFolderPaths(
      filteredFolders,
      restrictionParentPath,
    );

    // Only consider selected folders that are actually visible in the current filter
    const visibleSelectedFolders = selectedMetadata.filter((path) =>
      visiblePaths.includes(path),
    );

    return visibleSelectedFolders.filter((path) => {
      // A folder is "deepest" if no other selected folder has it as a prefix
      return !visibleSelectedFolders.some(
        (otherPath) => otherPath !== path && otherPath.startsWith(path + "/"),
      );
    });
  }, [
    selectedMetadata,
    filteredFolders,
    getFilteredFolderPaths,
    restrictionParentPath,
  ]);

  const handleFolderSelect = useCallback(
    (folderPath: string, folder: MetadataFolder, parentPath = "") => {
      const allChildPaths = getAllChildPaths(folder, parentPath);
      toggleFolderSelection(folderPath, allChildPaths);
    },
    [toggleFolderSelection, getAllChildPaths],
  );

  const toggleFolderExpansion = useCallback((folderPath: string) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderPath)) {
        newSet.delete(folderPath);
      } else {
        newSet.add(folderPath);
      }
      return newSet;
    });
  }, []);

  // Custom Select All state that only considers visible folders in current filter
  const getFilteredSelectAllState = useCallback(() => {
    const visiblePaths = getFilteredFolderPaths(
      filteredFolders,
      restrictionParentPath,
    );

    if (visiblePaths.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const selectedVisiblePaths = visiblePaths.filter((path) =>
      isMetadataSelected(path),
    );

    if (selectedVisiblePaths.length === 0) {
      return { checked: false, indeterminate: false };
    } else if (selectedVisiblePaths.length === visiblePaths.length) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  }, [
    filteredFolders,
    getFilteredFolderPaths,
    isMetadataSelected,
    restrictionParentPath,
  ]);

  const handleSelectAllToggle = useCallback(() => {
    const visiblePaths = getFilteredFolderPaths(
      filteredFolders,
      restrictionParentPath,
    );
    const selectAllState = getFilteredSelectAllState();

    if (selectAllState.checked) {
      // Unselect all visible folders: add them to excluded list
      const allPaths = getFilteredFolderPaths(metadataFolders);
      const newExcluded = [...new Set([...excludedMetadata, ...visiblePaths])];
      setExcludedMetadata(newExcluded);
    } else {
      // Select all visible folders: remove them from excluded list
      const newExcluded = excludedMetadata.filter(
        (path) => !visiblePaths.includes(path),
      );
      setExcludedMetadata(newExcluded);
    }
  }, [
    filteredFolders,
    getFilteredFolderPaths,
    getFilteredSelectAllState,
    excludedMetadata,
    setExcludedMetadata,
    metadataFolders,
    restrictionParentPath,
  ]);

  const renderFolderItem = useCallback(
    (folder: MetadataFolder, level = 0, parentPath = ""): React.JSX.Element => {
      const currentPath = parentPath
        ? `${parentPath}/${folder.folderName}`
        : folder.folderName;
      const hasSubfolders = folder.subfolders && folder.subfolders.length > 0;
      const isExpanded = expandedFolders.has(currentPath);
      const checkboxState = getCheckboxState(folder, currentPath, parentPath);
      const indentStyle = { paddingLeft: `${level * 12}px` };

      return (
        <div key={currentPath}>
          <Stack
            horizontal
            verticalAlign="start"
            style={{
              ...indentStyle,
              minHeight: "32px",
              alignItems: "flex-start",
            }}
          >
            {hasSubfolders ? (
              <IconButton
                onRenderIcon={() =>
                  isExpanded ? (
                    <ExpandMore sx={{ fontSize: 18 }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: 18 }} />
                  )
                }
                onClick={() => toggleFolderExpansion(currentPath)}
                styles={{
                  root: {
                    minWidth: 24,
                    height: 24,
                    flexShrink: 0,
                    marginTop: "4px",
                  },
                }}
              />
            ) : (
              <div
                style={{
                  width: 32,
                  height: 24,
                  minWidth: 24,
                  flexShrink: 0,
                  marginTop: "4px",
                }}
              />
            )}
            <Checkbox
              label={folder.folderName}
              checked={checkboxState.checked}
              indeterminate={checkboxState.indeterminate}
              onChange={() =>
                handleFolderSelect(currentPath, folder, parentPath)
              }
              styles={{
                root: {
                  marginLeft: 4,
                  flexGrow: 1,
                  minWidth: 0,
                  alignItems: "flex-start",
                },
                checkbox: {
                  marginTop: "4px",
                  flexShrink: 0,
                },
                text: {
                  fontSize: "13px",
                  lineHeight: "20px",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  paddingTop: "2px",
                },
              }}
            />
          </Stack>

          {hasSubfolders && isExpanded && (
            <div>
              {folder.subfolders!.map((subfolder) =>
                renderFolderItem(subfolder, level + 1, currentPath),
              )}
            </div>
          )}
        </div>
      );
    },
    [
      expandedFolders,
      selectedMetadata,
      toggleFolderExpansion,
      handleFolderSelect,
      getCheckboxState,
    ],
  );

  React.useEffect(() => {
    if (metadataError) {
      console.error("Metadata loading error:", metadataError);
    }
  }, [metadataError]);

  const deepestFolders = getDeepestFolders();

  useEffect(() => {
    onFolderStructureChanged(deepestFolders.length);
  }, [deepestFolders.length, onFolderStructureChanged]);

  const libraryTitle =
    useCaseConfig?.virtualLibraryTitle ||
    (currentDg ? `${currentDg} Virtual Library` : DEFAULT_FOLDER_LABEL);

  if (!isOpen) return null;

  return (
    <section className={styles.panel} aria-label="Library panel">
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        className={styles.header}
      >
        <span role="heading" aria-level={2} className={styles.title}>
          {libraryTitle}
        </span>
        <IconButtonCustom
          size="small"
          label="Hide library"
          icons={CLOSE_ICONS}
          onClick={onClose}
        />
      </Stack>
      <div className={styles.content}>
        {fetchingMetadata ? (
          <Text role="status">Loading folders...</Text>
        ) : metadataError ? (
          <Text role="alert">{metadataError}</Text>
        ) : filteredFolders.length > 0 ? (
          <>
            <div className={styles.selectAll}>
              <Checkbox
                label="Select All"
                checked={getFilteredSelectAllState().checked}
                indeterminate={getFilteredSelectAllState().indeterminate}
                onChange={handleSelectAllToggle}
                styles={{
                  root: {
                    alignItems: "flex-start",
                  },
                  checkbox: {
                    marginTop: "4px",
                    marginLeft: "6px",
                    flexShrink: 0,
                  },
                  text: {
                    fontSize: "13px",
                    lineHeight: "20px",
                    fontWeight: "600",
                    paddingTop: "2px",
                  },
                }}
              />
            </div>
            {/* Individual Folder Items */}
            {filteredFolders.map((folder) =>
              renderFolderItem(folder, 0, restrictionParentPath),
            )}
          </>
        ) : (
          <Text variant="small" styles={{ root: { color: "#666" } }}>
            No metadata folders available
          </Text>
        )}
      </div>
    </section>
  );
};

export default LibraryPanel;
